import React, { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Collapse,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  Popover,
  styled,
  Typography,
} from "@mui/material";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { PrimaryFormActionButton, PrimaryTooltip } from "../../../Components/StyledComponent";
import PrimaryChip from "../../../Components/PrimaryChip";
import SearchInput from "./SearchInput";
import FilterSelectBox from "./FilterSelectBox";
import CustomCheckbox from "./GlobalFilterCheckbox";
import {
  NODE_STATE,
  buildLeafIndex,
  collectAllLeafIds,
  countLeaves,
  filterTree,
  getNodeState,
  getSelectionSummary,
  toggleNode,
} from "./hierarchyFilterUtils";

const popoverPaperStyles = {
  borderRadius: "10px",
  backgroundColor: "#FFFFFF",
  boxShadow:
    "2px 2px 4px 0px rgba(151, 151, 151, 0.25), -2px -2px 4px 0px rgba(151, 151, 151, 0.25)",
  overflow: "hidden",
};

const StyledListItem = styled(ListItem)(() => ({
  paddingLeft: 0,
}));

// A leaf sits under the expander gutter its parent's arrow occupies. A flat
// list has no arrow anywhere, so it drops the gutter and starts at the edge.
const StyledFormControlLabel = styled(FormControlLabel, {
  shouldForwardProp: (prop) => prop !== "hasChildren" && prop !== "isFlat",
})(({ hasChildren, isFlat }) => ({
  marginLeft: isFlat ? 0 : hasChildren ? "6px" : "calc(6px + 10px)",
}));

const ExpandCollapseIcon = styled(ArrowRightIcon, {
  shouldForwardProp: (prop) => prop !== "isExpanded",
})(({ isExpanded }) => ({
  transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
  fontSize: "22px",
}));

const EMPTY_EXPANDED = {};

/**
 * One filter dimension: a trigger, a panel with a searchable tri-state tree, and
 * its own draft / apply cycle. Depth-agnostic, so the same component renders the
 * flat segment and customer lists and the four-level location tree.
 *
 * Selection is leaf ids only; every parent checkbox state is derived.
 */
const HierarchyFilterDropdown = ({
  fieldLabel,
  panelTitle,
  allSelectedLabel,
  searchPlaceholder,
  nodes = [],
  appliedLeafIds = [],
  onApply,
  defaultExpandedKeys = EMPTY_EXPANDED,
  variant = "popover",
  testId = "hierarchy-filter",
}) => {
  const isAccordion = variant === "accordion";
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const [draft, setDraft] = useState(() => new Set(appliedLeafIds));
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(defaultExpandedKeys);

  const leafIndex = useMemo(() => buildLeafIndex(nodes), [nodes]);
  // Segment and customer are flat lists; location is a tree.
  const isFlat = useMemo(() => nodes.every((node) => !node.children?.length), [nodes]);
  const totalLeafCount = useMemo(() => countLeaves(nodes), [nodes]);

  const { nodes: visibleNodes, expandedKeys: searchExpandedKeys } = useMemo(
    () => filterTree(nodes, search),
    [nodes, search]
  );

  const summary = useMemo(
    () => getSelectionSummary(appliedLeafIds, nodes, allSelectedLabel),
    [appliedLeafIds, nodes, allSelectedLabel]
  );

  // Seed the draft from the applied selection. The popover seeds each time it
  // opens, which is what makes Cancel and click-outside discard the draft: only
  // Apply ever calls back out.
  useEffect(() => {
    if (!isAccordion && !open) return;
    setDraft(new Set(appliedLeafIds));
    setSearch("");
    setExpanded(defaultExpandedKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAccordion, appliedLeafIds, nodes]);

  // Searching auto-expands the ancestors of every match; clearing it restores
  // the default expansion state.
  useEffect(() => {
    setExpanded(search.trim() ? searchExpandedKeys : defaultExpandedKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, searchExpandedKeys]);

  const toggleExpand = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const handleSelectAll = () => setDraft(new Set(collectAllLeafIds(nodes)));
  const handleClearAll = () => setDraft(new Set());

  const handleCancel = () => {
    if (!isAccordion) setAnchorEl(null);
    else setDraft(new Set(appliedLeafIds));
  };

  const handleApply = () => {
    onApply([...draft]);
    if (!isAccordion) setAnchorEl(null);
  };

  const renderNodes = (nodeList, depth) =>
    nodeList.map((node) => {
      const hasChildren = !!(node.children && node.children.length);
      const state = getNodeState(node.key, leafIndex, draft);
      return (
        <React.Fragment key={node.key}>
          <StyledListItem dense sx={{ pl: depth * 2 }}>
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={() => toggleExpand(node.key)}
                sx={{ p: 0 }}
                data-testid={`${testId}-expand-${node.key}`}
              >
                <ExpandCollapseIcon isExpanded={!!expanded[node.key]} />
              </IconButton>
            ) : (
              !isFlat && (
                <span
                  style={{ width: 22, display: "inline-block" }}
                  data-testid={`${testId}-gutter-${node.key}`}
                />
              )
            )}
            <ListItemIcon sx={{ minWidth: "auto" }}>
              <StyledFormControlLabel
                hasChildren={hasChildren}
                isFlat={isFlat}
                control={
                  <CustomCheckbox
                    checked={state === NODE_STATE.CHECKED}
                    indeterminate={state === NODE_STATE.INDETERMINATE}
                    onChange={() => setDraft((prev) => toggleNode(node.key, leafIndex, prev))}
                  />
                }
                label={
                  <Typography
                    variant="fw400s12c494949"
                    sx={{ lineHeight: "24px", whiteSpace: "nowrap" }}
                  >
                    {node.name}
                  </Typography>
                }
              />
            </ListItemIcon>
          </StyledListItem>
          {hasChildren && (
            <Collapse in={!!expanded[node.key]} timeout="auto" unmountOnExit>
              <List disablePadding>{renderNodes(node.children, depth + 1)}</List>
            </Collapse>
          )}
        </React.Fragment>
      );
    });

  const panelHeaderActions = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <Typography
        variant="fw500s12lh24c6D72F6"
        sx={{ cursor: "pointer" }}
        onClick={handleSelectAll}
        data-testid={`${testId}-select-all`}
      >
        Select all
      </Typography>
      <Box sx={{ width: "1px", height: "12px", backgroundColor: "#DEDEDE" }} />
      <Typography
        variant="fw500s12lh24c6D72F6"
        sx={{ cursor: "pointer", color: "#686868" }}
        onClick={handleClearAll}
        data-testid={`${testId}-clear-all`}
      >
        Clear all
      </Typography>
    </Box>
  );

  const treeBox = (sizing) => (
    <Box sx={{ ...sizing, overflowY: "auto", overflowX: "auto", "& ul": { minWidth: "max-content" } }}>
      {visibleNodes.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 2,
          }}
        >
          <Typography variant="fw400s12c494949" sx={{ color: "#A2A2A2" }}>
            No matches found
          </Typography>
        </Box>
      ) : (
        <List disablePadding>{renderNodes(visibleNodes, 0)}</List>
      )}
    </Box>
  );

  const footer = (
    <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 1.5 }}>
      <Typography
        variant="fw500s12lh24c6D72F6"
        sx={{ cursor: "pointer", color: "#686868" }}
        onClick={handleCancel}
        data-testid={`${testId}-cancel`}
      >
        Cancel
      </Typography>
      <PrimaryFormActionButton
        onClick={handleApply}
        disabled={draft.size === 0}
        sx={isAccordion ? { py: 0.75, px: 2, fontSize: "12px" } : undefined}
        data-testid={`${testId}-apply`}
      >
        Apply
      </PrimaryFormActionButton>
    </Box>
  );

  if (isAccordion) {
    return (
      <Accordion
        sx={{
          boxShadow: "none",
          borderBottom: "1px solid #E6E6E6",
          my: 0,
          "&:before": { display: "none" },
          "&.Mui-expanded": { my: 0 },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            paddingTop: "18px",
            paddingBottom: "18px",
            alignItems: "flex-start",
            "&.Mui-expanded": { minHeight: "auto" },
            "& .MuiAccordionSummary-content": { margin: 0, alignItems: "flex-start", gap: 1 },
          }}
          data-testid={`${testId}-trigger`}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Typography
              variant="fw400s14c000000"
              sx={{ lineHeight: "16px", color: "#1C1C1C", mr: 1 }}
            >
              {fieldLabel}
            </Typography>
            {appliedLeafIds.length > 0 && appliedLeafIds.length !== totalLeafCount && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}
                onClick={(e) => e.stopPropagation()}
              >
                <PrimaryChip
                  label={summary.label}
                  tooltipTitle={summary.tooltip}
                  tooltipPlacement="bottom"
                  showDelete={false}
                />
              </Box>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Typography variant="fw700s12lh24c3D4B5A">{panelTitle}</Typography>
              {panelHeaderActions}
            </Box>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
            />
            {treeBox({ maxHeight: 150 })}
            {footer}
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <>
      <PrimaryTooltip
        title={summary.tooltip}
        componentsProps={{ tooltip: { sx: { whiteSpace: "pre-line" } } }}
      >
        <Box>
          <FilterSelectBox
            open={open}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            label={fieldLabel}
            displayValue={summary.label}
            maxWidth={200}
            dataTestId={`${testId}-trigger`}
          />
        </Box>
      </PrimaryTooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        slotProps={{ paper: { sx: popoverPaperStyles } }}
      >
        <Box sx={{ width: "320px" }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              py: 1.5,
              borderBottom: "1px solid #F0F0F0",
            }}
          >
            <Typography variant="fw700s14lh24c3D4B5A">{panelTitle}</Typography>
            {panelHeaderActions}
          </Box>

          <Box sx={{ px: 2, py: 1.5 }}>
            <Box sx={{ mb: 1.5 }}>
              <SearchInput
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={searchPlaceholder}
              />
            </Box>
            {treeBox({ height: "200px" })}
          </Box>

          <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #F0F0F0" }}>{footer}</Box>
        </Box>
      </Popover>
    </>
  );
};

export default HierarchyFilterDropdown;
